/* Package JSON Import will be here */
import React, { useState, useEffect } from "react";
import { getTranslate } from "react-localize-redux";
import { useSelector, useDispatch } from "react-redux";
import {
    Drawer,
    Button,
    Space,
    Form,
    Row,
    Col,
    Input,
    DatePicker,
    Select,
    Image,
    Spin,
    Badge,
    ConfigProvider
} from "antd";
import moment from 'moment';
import { isEmpty } from "lodash";
import enUS from 'antd/lib/locale/en_US';
import esES from 'antd/lib/locale/es_ES';
/* Package JSON Import will be here */

/* Project Import will be here */
import Constants from "utilities/appConstants";
import ImageUpload from 'containers/common/ImageUpload';
import { getGenerateComplaintsMetaData, generateNewComplaint } from "../../reducers/complaints/generateComplaints";
import { Spinner } from "components/Spinner";
import NoData from "components/reusable/NoData";
import { IGenerateComplaintModel } from "models/complaintModels";
import { errorToast } from "utilities/utils";
import { moveImageFromTempToMain } from "hooks/uploadHelper";
import placeholderImg from "assets/img/banner-placeholder.png";
/* Project Import will be here */

/* Styles will be here */
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
/* Styles will be here */

const GenerateComplaint = (props) => {
    const [isEnableImageUpload] = useState(false);
    const [isApiMethodCalled, setApiMethodCalled] = useState(false);
    const [imageUploadIsVisible, setImageUploadIsVisible] = useState(false);
    const [complaintImageUrls, setComplaintImageUrls] = useState({ images: [] });
    const [isUploading, setIsUploading] = useState(false);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 767 ? true : false);
    const [isTabView, setIsTabView] = useState((window.innerWidth > 767 && window.innerWidth <= 990) ? true : false);
    const [isMidView, setIsMidView] = useState((window.innerWidth > 990 && window.innerWidth <= 1300) ? true : false);

    const { translate, appSettings, generateComplaints, awsConfig, languageCode } = useSelector((state) => ({
        generateComplaints: state.generateComplaints,
        appSettings: state.appSettings,
        awsConfig: state.appSettings.awsConfig,
        translate: getTranslate(state.locale),
        languageCode: state.localStore
    }));

    const dispatch = useDispatch();

    useEffect(() => {
        window.addEventListener('resize', handleWindowSizeChange);

        /* Make an API call to fetch unpaid invoice list */
        /* Only make API call if there is no meta data available */
        if (isEmpty(generateComplaints.data))
            dispatch(getGenerateComplaintsMetaData());

        setApiMethodCalled(true);

        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
        }
    }, []);

    const handleWindowSizeChange = () => {
        setIsMobileView(window.innerWidth < 767 ? true : false);
        setIsTabView((window.innerWidth > 767 && window.innerWidth <= 990) ? true : false);
        setIsMidView((window.innerWidth > 990 && window.innerWidth <= 1200) ? true : false);
    }

    const getFormInitialValues = () => {
        return {
            SKUName: props.selectedSku.Name === undefined ? props.selectedSku.ProductName : props.selectedSku.Name,
            complaintDate: moment()
        };
    }

    /* Handle on submit the generate complaint form */
    const onSubmitGenerateComplaintForm = async (values: IGenerateComplaintModel) => {
        /* Check if imageUrls is defined and not an empty array */
        let isError = false;
        let newComplaintImageUrls = [];
        if (complaintImageUrls.images.length > 0) {
            const imageRes = await Promise.all(
                complaintImageUrls.images.map(async (img) => {
                    const imageUrl = await moveImageFromTempToMain(awsConfig, img, `${awsConfig.RootDirComplaint}/`);
                    return {
                        imageUrl: imageUrl.updatedUrl,
                        status: imageUrl.status
                    };
                })
            );

            /* Check for image movement error */
            if (imageRes) {
                isError = imageRes.some((img) => !img.status);
            }

            if (isError) {
                errorToast({ content: translate("common.error.fileMovement") });
                return;
            }

            newComplaintImageUrls = imageRes.map(obj => obj.imageUrl);
        }

        /* Assign the uploaded images here */
        values.images = newComplaintImageUrls;

        /* Get the from info */
        values.invoiceDate = values.invoiceDate.format(Constants.YYYY_MM_DD);
        values.productId = props.selectedSku.ProductId;
        const selectedSkuName = values.SKUName;
        delete values.SKUName;

        /* Make API call to generate new complaint */
        dispatch(generateNewComplaint(values, selectedSkuName, props.setIsShowGenerateComplaint));
    };

    const disableFeaturedDate = (current) => {
        /* Can not select days before today and today */
        return (current && current > moment().endOf('day'));
    }

    /* Open camera or upload from device option to upload the complaint images */
    const openImageUpload = () => {
        if (complaintImageUrls.images.length < appSettings.complaintUploadImageCount) {
            setImageUploadIsVisible(true);
        }
        else {
            errorToast({ content: translate("complaints.generateComplaints.complaintImgLengthValidation", { count: appSettings.complaintUploadImageCount }) });
        }
    };

    const imageUrlCallback = (uploadedImageUrl) => {
        if (uploadedImageUrl !== "") {
            let uploadedImgList = complaintImageUrls.images;
            uploadedImgList.push(uploadedImageUrl);

            setComplaintImageUrls({ images: uploadedImgList });
        }
    };

    const removeImage = (index) => {
        let deleteImageList = complaintImageUrls.images;
        deleteImageList.splice(index, 1);

        setComplaintImageUrls({ images: deleteImageList });
    }

    /* TODO: Need to write this a generic datepicker */
    const customLocale = () => {
        if (languageCode.defaultLang === "es") {
            return {
                ...esES,
                DatePicker: {
                    ...esES.DatePicker,
                    lang: {
                        ...esES.DatePicker.lang,
                        today: translate("session.common.today"),
                    },
                },
            };
        } else {
            return {
                ...enUS,
                DatePicker: {
                    ...enUS.DatePicker,
                    lang: {
                        ...enUS.DatePicker.lang,
                        today: translate("session.common.today"),
                    },
                },
            };
        }
    };

    const renderComponent = () => {
        if (!isApiMethodCalled || generateComplaints.isLoading) {
            return (
                <React.Fragment>
                    <div className="container pt-5">
                        <Spinner message={translate("complaints.generateComplaints.loading")} />
                    </div>
                </React.Fragment>
            );
        }
        else if (isEmpty(generateComplaints.data)) {
            return (
                <React.Fragment>
                    <div className="container pt-2">
                        <NoData NoDataMessage={translate("complaints.generateComplaints.noData")} />
                    </div>
                </React.Fragment>
            );
        }
        else {
            return (
                <Spin tip={translate("complaints.generateComplaints.savingComplaintLoading")} spinning={generateComplaints.isLoadingGenerateNewComplaint}>
                    <Form
                        name="generateComplaintForm"
                        initialValues={getFormInitialValues()}
                        onFinish={onSubmitGenerateComplaintForm}
                        layout="vertical"
                        className="complaint-form"
                    >
                        <div className="p-3">
                            <Row>
                                <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={translate("complaints.labels.skuName")}
                                        name="SKUName"
                                        rules={[{ required: true, message: translate("complaints.validation.skuName") }]}
                                    >
                                        <Input size="large" disabled placeholder={translate("complaints.labels.skuName")} />
                                    </Form.Item>
                                </Col>

                                <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={translate("complaints.labels.retailerInvNo")}
                                        name="invoiceNo"
                                        rules={[
                                            {
                                                validator: (event, value) =>
                                                    value && (value.trim() !== value)
                                                        ? Promise.reject(translate("complaints.validation.noSpace"))
                                                        : Promise.resolve(),
                                            }, {
                                                required: false,
                                                pattern: /^[^*|":<>[=/?^/\]{}`\\()';@&$]+$/,
                                                message: translate("complaints.validation.allowedSpecialChar")
                                            }
                                        ]}
                                    >
                                        <Input
                                            size="large"
                                            placeholder={translate("complaints.labels.retailerInvNo")}
                                            maxLength={Constants.COMPLAINT_MAX_LENGTH.InvoiceNo}
                                            autoComplete="off"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col span={24} md={12} className="p-1">
                                    <ConfigProvider locale={customLocale()}>
                                        <Form.Item
                                            label={`${translate("complaints.labels.retailerInvoiceDate")} *`}
                                            name="invoiceDate"
                                            rules={[{ required: true, message: translate("complaints.validation.retailerInvoiceDate") }]}
                                        >
                                            <DatePicker
                                                size="large"
                                                inputReadOnly={true}
                                                allowClear={false}
                                                dropdownClassName="common-datePicker"
                                                format={Constants.DD_MM_YYYY}
                                                className="w-100"
                                                disabledDate={disableFeaturedDate}
                                                placeholder={translate("complaints.labels.retailerInvoiceDate")}
                                            />
                                        </Form.Item>
                                    </ConfigProvider>
                                </Col>

                                <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={`${translate("complaints.labels.complaintReason")} *`}
                                        name="complaintReasonId"
                                        rules={[{ required: true, message: translate("complaints.validation.complaintReason") }]}
                                    >
                                        <Select
                                            placeholder={translate("complaints.labels.complaintReason")}
                                            size="large"
                                        >
                                            {generateComplaints.data.ComplaintReasons.map((item) => {
                                                return (<Select.Option key={item.Id} value={item.Id}>{item.Value}</Select.Option>)
                                            })}
                                        </Select>
                                    </Form.Item>
                                </Col>

                                <Col span={24} className="p-1">
                                    <Form.Item
                                        label={`${translate("complaints.labels.remarks")} *`}
                                        name="remarks"
                                        rules={[
                                            { required: true, message: translate("complaints.validation.complaintRemarks") },
                                            {
                                                validator: (event, value) =>
                                                    value && (value.trim() !== value)
                                                        ? Promise.reject(translate("complaints.validation.noSpace"))
                                                        : Promise.resolve(),
                                            },
                                            {
                                                required: false,
                                                pattern: /^[^*|":<>[=/?^/\]{}`\\()';@&$]+$/,
                                                message: translate("complaints.validation.allowedSpecialChar")
                                            }
                                        ]}
                                    >
                                        <Input.TextArea
                                            rows={4}
                                            size="large"
                                            placeholder={translate("complaints.labels.remarks")}
                                            maxLength={Constants.COMPLAINT_MAX_LENGTH.Remarks}
                                            autoComplete="off"
                                        />
                                    </Form.Item>
                                </Col>

                                {/* <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={translate("complaints.labels.complaintType")}
                                        name="complaintTypeId"
                                    >
                                        <Select
                                            size="large"
                                            placeholder={translate("complaints.labels.complaintType")}
                                        >
                                            {generateComplaints.data.ComplaintTypes.map((item) => {
                                                return (<Select.Option key={item.Id} value={item.Id}>{item.Value}</Select.Option>)
                                            })}
                                        </Select>
                                    </Form.Item>
                                </Col> */}

                                {/* <Col span={24} md={12} className="p-1">
                                    <ConfigProvider locale={customLocale()}>
                                        <Form.Item
                                            label={translate("complaints.labels.complaintDate")}
                                            name="complaintDate"
                                            rules={[{ required: true, message: translate("complaints.validation.complaintDate") }]}
                                        >
                                            <DatePicker
                                                size="large"
                                                disabled
                                                inputReadOnly={true}
                                                allowClear={false}
                                                dropdownClassName="common-datePicker"
                                                format={Constants.DD_MM_YYYY}
                                                className="w-100"
                                                disabledDate={disableFeaturedDate}
                                                placeholder={translate("complaints.labels.complaintDate")}
                                            />
                                        </Form.Item>
                                    </ConfigProvider>
                                </Col> */}

                                {isEnableImageUpload &&
                                    <Col span={24} md={24} className="p-1">
                                        <Form.Item
                                            label={translate("complaints.labels.attachImage")}
                                        >
                                            <Spin tip={translate('common.imageUpload.loading')} spinning={isUploading}>
                                                <div className="w-100 text-center program-upload-btn" onClick={openImageUpload}>
                                                    {translate("common.buttons.uploadPicture")}
                                                    <i className="fa fa-picture-o fa-lg pl-3" aria-hidden="true"></i>
                                                </div>

                                                <Row className="p-1">
                                                    {complaintImageUrls.images.length > 0 &&
                                                        complaintImageUrls.images.map((complaintImgUrl, index) => {
                                                            return (
                                                                <Col span={8} md={8} lg={8} className="p-1">
                                                                    <Image
                                                                        className="complaint-img"
                                                                        src={complaintImgUrl}
                                                                        fallback={placeholderImg}
                                                                    />
                                                                    <Badge
                                                                        className="complaint-img-close-badge"
                                                                        count={<i className="fa fa-times-circle font-25" aria-hidden="true"></i>}
                                                                        onClick={() => removeImage(index)}
                                                                    />
                                                                </Col>
                                                            );
                                                        })}
                                                </Row>
                                            </Spin>
                                        </Form.Item>
                                    </Col>
                                }

                                <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={`${translate("complaints.labels.consumerName")} *`}
                                        name="consumerName"
                                        rules={[
                                            { required: true, message: translate("complaints.validation.consumerName") },
                                            {
                                                required: false,
                                                pattern: /^[^*|":<>[=/?^/\]{}`\\()';@&$]+$/,
                                                message: translate("complaints.validation.allowedSpecialChar")
                                            },
                                            {
                                                validator: (event, value) =>
                                                    value && (value.trim() !== value)
                                                        ? Promise.reject(translate("complaints.validation.noSpace"))
                                                        : Promise.resolve(),
                                            }
                                        ]}
                                    >
                                        <Input
                                            size="large"
                                            placeholder={translate("complaints.labels.consumerName")}
                                            maxLength={Constants.COMPLAINT_MAX_LENGTH.ConsumerName}
                                            autoComplete="off"
                                        />
                                    </Form.Item>
                                </Col>

                                <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={`${translate("complaints.labels.consumerContactNo")} *`}
                                        name="consumerMobile"
                                        rules={[{ required: true, pattern: new RegExp(`^(\\+\\d{1,3}[- ]?)?\\d{${appSettings.minMobileLength},}$`), message: translate("complaints.validation.consumerContactNo") }]}
                                    >
                                        <Input
                                            size="large"
                                            inputMode="numeric"
                                            maxLength={appSettings.maxMobileLength}
                                            placeholder={translate("complaints.labels.consumerContactNo")}
                                            autoComplete="off"
                                        />
                                    </Form.Item>
                                </Col>

                                {/* <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={translate("complaints.labels.methodOfDisposal")}
                                        name="disposalTypeId"
                                    >
                                        <Select
                                            size="large"
                                            placeholder={translate("complaints.labels.methodOfDisposal")}
                                        >
                                            {generateComplaints.data.DisposalMethods.map((item) => {
                                                return (<Select.Option key={item.Id} value={item.Id}>{item.Value}</Select.Option>)
                                            })}
                                        </Select>
                                    </Form.Item>
                                </Col> */}

                                {/* <Col span={24} md={12} className="p-1">
                                    <Form.Item
                                        label={translate("complaints.labels.productReturnTo")}
                                        name="returnTypeId"
                                    >
                                        <Select
                                            size="large"
                                            placeholder={translate("complaints.labels.productReturnTo")}
                                        >
                                            {generateComplaints.data.ReturnTo.map((item) => {
                                                return (<Select.Option key={item.Id} value={item.Id}>{item.Value}</Select.Option>)
                                            })}
                                        </Select>
                                    </Form.Item>
                                </Col> */}
                            </Row>

                            <Row className="p-3">
                                <Col span={24} className="p-1">
                                    <Form.Item>
                                        <div className="text-right">
                                            <Space>
                                                <Button
                                                    shape="round"
                                                    size="large"
                                                    disabled={isUploading}
                                                    onClick={() => props.setIsShowGenerateComplaint(false)}
                                                >
                                                    {translate("common.buttons.cancel")}
                                                </Button>
                                                <Button
                                                    className="float-right"
                                                    type="primary"
                                                    htmlType="submit"
                                                    size="large"
                                                    loading={isUploading || generateComplaints.isLoadingGenerateNewComplaint}
                                                    disabled={isUploading}
                                                    shape="round"
                                                >
                                                    {translate("common.buttons.generate")}
                                                </Button>
                                            </Space>
                                        </div>
                                    </Form.Item>
                                </Col>
                            </Row>
                        </div>
                    </Form>
                </Spin>
            );
        }
    };

    return (
        <React.Fragment>
            <Drawer
                title={
                    <div className="flex items-center">
                        <i
                            className="fa fa-chevron-left mr-4 cursor-pointer"
                            aria-hidden="true"
                            onClick={() => props.setIsShowGenerateComplaint(false)}
                        ></i>
                        {translate("common.links.generateComplaint")}
                    </div>
                }
                placement="right"
                visible={props.isShowGenerateComplaint}
                className="generate-complaint-drawer"
                closable={false}
                width={isMobileView ? "100%" : (isTabView ? "80%" : isMidView ? "60%" : "40%")}
            >
                {renderComponent()}
            </Drawer>

            {imageUploadIsVisible &&
                <ImageUpload
                    title={translate("common.buttons.uploadPicture")}
                    setIsUploading={setIsUploading}
                    isVisible={imageUploadIsVisible}
                    setIsVisible={setImageUploadIsVisible}
                    setS3ImageUrl={imageUrlCallback}
                    type={Constants.ROOT_DIR_COMPLAINT}
                ></ImageUpload>
            }
        </React.Fragment>
    );
};

export default GenerateComplaint;